import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Tooltip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as XLSX from 'xlsx';
import traccarApi from '../../../../services/traccarApi';
import useMapStyles from '../../useMapStyles';

const ALL_COLUMNS = [
  { key: 'fixTime',   label: 'Hora ajustada' },
  { key: 'latitude',  label: 'Latitud' },
  { key: 'longitude', label: 'Longitud' },
  { key: 'speed',     label: 'Velocidad' },
  { key: 'address',   label: 'Dirección Calle' },
  { key: 'altitude',  label: 'Altitud' },
];

const DEFAULT_COLUMNS = ['fixTime', 'latitude', 'longitude', 'speed', 'address'];

const MAP_CSS = `
  .maplibregl-popup-content {
    padding: 0 !important; border-radius: 8px !important;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    overflow: hidden; min-width: 200px;
  }
  .maplibregl-popup-close-button { font-size: 16px; color: #9e9e9e; padding: 4px 8px; }
  .rr-popup-header { padding: 10px 14px 8px; border-bottom: 1px solid #f0f0f0; }
  .rr-popup-title { margin: 0; font-size: 13px; font-weight: 600; color: #212121; padding-right: 20px; }
  .rr-popup-body { padding: 10px 14px 12px; display: flex; flex-direction: column; gap: 5px; }
  .rr-popup-row { display: flex; justify-content: space-between; gap: 8px; }
  .rr-popup-label { font-size: 11px; color: #9e9e9e; white-space: nowrap; }
  .rr-popup-value { font-size: 12px; color: #212121; font-weight: 500; text-align: right; }
`;

function formatTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d)) return isoString;
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).replace(',', '');
}

function formatSpeed(knots) {
  if (knots === undefined || knots === null) return '';
  return `${(Number(knots) * 1.852).toFixed(2)} km/h`;
}

function formatAltitude(m) {
  if (m === undefined || m === null) return '';
  return `${Number(m).toFixed(0)} m`;
}

function getCellValue(row, colKey) {
  switch (colKey) {
    case 'fixTime':   return formatTime(row.fixTime || row.deviceTime || row.serverTime);
    case 'latitude':  return row.latitude  !== undefined ? `${Number(row.latitude).toFixed(6)}°`  : '';
    case 'longitude': return row.longitude !== undefined ? `${Number(row.longitude).toFixed(6)}°` : '';
    case 'speed':     return formatSpeed(row.speed);
    case 'address':   return row.address || '';
    case 'altitude':  return formatAltitude(row.altitude);
    default:          return '';
  }
}

function getRawValue(row, colKey) {
  switch (colKey) {
    case 'fixTime':   return formatTime(row.fixTime || row.deviceTime || row.serverTime);
    case 'latitude':  return row.latitude  !== undefined ? Number(row.latitude).toFixed(6)  : '';
    case 'longitude': return row.longitude !== undefined ? Number(row.longitude).toFixed(6) : '';
    case 'speed':     return row.speed     !== undefined ? (Number(row.speed) * 1.852).toFixed(2) : '';
    case 'address':   return row.address   || '';
    case 'altitude':  return row.altitude  !== undefined ? Number(row.altitude).toFixed(0) : '';
    default:          return '';
  }
}

function getServiceCode(pos) {
  try {
    const raw = pos?.attributes?.service_info;
    if (!raw) return null;
    const info = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return info?.service_code || null;
  } catch (_) {
    return null;
  }
}

const RouteMap = ({ positions, highlightIndex, styles: mapStyles }) => {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const popupRef     = useRef(null);
  const markerRef    = useRef(null);
  const loadedRef    = useRef(false);
  const styleUrl     = mapStyles?.[0]?.url;

  useEffect(() => {
    if (!styleUrl || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: styleUrl,
      center: [-99.1332, 19.4326],
      zoom: 12,
      attributionControl: false,
      fadeDuration: 0,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    popupRef.current = new maplibregl.Popup({ offset: 20, closeButton: true, closeOnClick: false });

    map.on('load', () => {
      loadedRef.current = true;
      map.addSource('route-src', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: 'route-bg', type: 'line', source: 'route-src',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.6 } });
      map.addLayer({ id: 'route-line', type: 'line', source: 'route-src',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-width': 4,
          'line-opacity': 0.95,
          'line-color': [
            'interpolate', ['linear'],
            ['get', 'speed'],
            0,   '#22c55e',
            20,  '#84cc16',
            40,  '#eab308',
            70,  '#f97316',
            100, '#ef4444',
          ],
        },
      });
    });

    return () => { map.remove(); mapRef.current = null; loadedRef.current = false; };
  }, [styleUrl]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !positions.length) return;

    const applyRoute = () => {
      const src = map.getSource('route-src');
      if (!src) return;
      const valid = positions.filter(p => p.latitude && p.longitude);
      const features = [];
      for (let i = 0; i < valid.length - 1; i++) {
        const a = valid[i], b = valid[i + 1];
        const speedKmh = (Number(a.speed) || 0) * 1.852;
        features.push({
          type: 'Feature',
          properties: { speed: speedKmh },
          geometry: {
            type: 'LineString',
            coordinates: [
              [Number(a.longitude), Number(a.latitude)],
              [Number(b.longitude), Number(b.latitude)],
            ],
          },
        });
      }
      src.setData({ type: 'FeatureCollection', features });
      if (valid.length) {
        const coords = valid.map(p => [Number(p.longitude), Number(p.latitude)]);
        const bounds = coords.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(coords[0], coords[0]));
        map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 600 });
      }
    };

    if (map.isStyleLoaded()) {
      applyRoute();
    } else {
      map.once('load', applyRoute);
    }
  }, [positions]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    popupRef.current?.remove();
    if (highlightIndex === null || highlightIndex === undefined) return;
    const pos = positions[highlightIndex];
    if (!pos?.latitude || !pos?.longitude) return;
    const el = document.createElement('div');
    el.style.cssText = 'width:18px;height:18px;border-radius:50%;background:#1976d2;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);cursor:pointer;';
    markerRef.current = new maplibregl.Marker({ element: el })
      .setLngLat([Number(pos.longitude), Number(pos.latitude)])
      .addTo(map);
    const html = `
      <div class="rr-popup-header">
        <p class="rr-popup-title">${formatTime(pos.fixTime || pos.deviceTime)}</p>
      </div>
      <div class="rr-popup-body">
        <div class="rr-popup-row">
          <span class="rr-popup-label">Velocidad</span>
          <span class="rr-popup-value">${formatSpeed(pos.speed)}</span>
        </div>
        ${pos.address ? `<div class="rr-popup-row"><span class="rr-popup-label">Dirección</span><span class="rr-popup-value" style="max-width:150px">${pos.address}</span></div>` : ''}
      </div>`;
    popupRef.current?.setLngLat([Number(pos.longitude), Number(pos.latitude)]).setHTML(html).addTo(map);
    map.easeTo({ center: [Number(pos.longitude), Number(pos.latitude)], zoom: Math.max(map.getZoom(), 15), duration: 500 });
  }, [highlightIndex, positions]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

const RouteReport = ({ devices: propDevices = [], positions: propPositions = [] }) => {
  const mapStyles = useMapStyles();

  const [serviceCode,     setServiceCode]     = useState('');
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_COLUMNS);
  const [rows,            setRows]            = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [searched,        setSearched]        = useState(false);
  const [page,            setPage]            = useState(0);
  const [rowsPerPage,     setRowsPerPage]     = useState(10);
  const [highlightIndex,  setHighlightIndex]  = useState(null);
  const [showMap,         setShowMap]         = useState(false);
  const [menuAnchor,      setMenuAnchor]      = useState(null);

  useEffect(() => {
    const id = 'route-report-css';
    if (!document.getElementById(id)) {
      const s = document.createElement('style'); s.id = id; s.textContent = MAP_CSS;
      document.head.appendChild(s);
    }
  }, []);

  const fetchPositions = useCallback(async () => {
    const code = serviceCode.trim();
    if (!code) return;

    setLoading(true);
    setHighlightIndex(null);
    setShowMap(false);
    setRows([]);

    try {
      const to   = new Date();
      const from = new Date(to.getTime() - 2 * 365 * 24 * 60 * 60 * 1000);

      console.log('[RouteReport] Fetching serviceCode:', code);
      const routeData = await traccarApi.getReport('route', {
        from: from.toISOString(),
        to:   to.toISOString(),
        serviceCode: code,
      });

      const sorted = (routeData || []).sort((a, b) => {
        const t1 = new Date(a.fixTime || a.deviceTime || 0).getTime();
        const t2 = new Date(b.fixTime || b.deviceTime || 0).getTime();
        return t1 - t2;
      });

      console.log('[RouteReport] Posiciones encontradas:', sorted.length);
      setRows(sorted);
      setPage(0);
      setSearched(true);
    } catch (err) {
      console.error('[RouteReport] Error:', err);
      setRows([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, [serviceCode]);

  const handleExport = useCallback(() => {
    setMenuAnchor(null);
    if (!rows.length) return;
    const visibleCols = ALL_COLUMNS.filter(c => selectedColumns.includes(c.key));
    const ws = XLSX.utils.aoa_to_sheet([
      visibleCols.map(c => c.label),
      ...rows.map(row => visibleCols.map(c => getRawValue(row, c.key))),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ruta');
    XLSX.writeFile(wb, `Reporte_Ruta_${serviceCode}_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, [rows, selectedColumns, serviceCode]);

  const handleMostrar       = () => { setMenuAnchor(null); fetchPositions(); };
  const handleRowHighlight  = (globalIndex) => { setHighlightIndex(globalIndex); setShowMap(true); };
  const handleColumnsChange = (e) => {
    const val = e.target.value;
    setSelectedColumns(typeof val === 'string' ? val.split(',') : val);
  };

  const visibleCols = ALL_COLUMNS.filter(c => selectedColumns.includes(c.key));
  const pagedRows   = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {showMap && rows.length > 0 && (
        <Box sx={{ height: '280px', flexShrink: 0, borderBottom: '1px solid #ddd', position: 'relative' }}>
          <RouteMap positions={rows} highlightIndex={highlightIndex} styles={mapStyles} />
          <IconButton
            size="small"
            onClick={() => { setShowMap(false); setHighlightIndex(null); }}
            sx={{
              position: 'absolute', top: 8, left: 8, zIndex: 10,
              bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            ✕
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.5,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: 'white',
          flexWrap: 'wrap',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <TextField
          label="Código de Servicio"
          value={serviceCode}
          onChange={e => setServiceCode(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchPositions()}
          size="small"
          sx={{ width: 200 }}
          placeholder="TP-XXX"
          inputProps={{ autoComplete: 'off' }}
        />
        <FormControl size="small" sx={{ width: 260 }}>
          <InputLabel>Columnas</InputLabel>
          <Select
            multiple
            value={selectedColumns}
            onChange={handleColumnsChange}
            input={<OutlinedInput label="Columnas" />}
            renderValue={(selected) =>
              ALL_COLUMNS.filter(c => selected.includes(c.key)).map(c => c.label).join(', ')
            }
            MenuProps={{ style: { zIndex: 9999 } }}
          >
            {ALL_COLUMNS.map(col => (
              <MenuItem key={col.key} value={col.key} dense>
                <Checkbox checked={selectedColumns.includes(col.key)} size="small" />
                <ListItemText primary={col.label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ButtonGroup variant="contained" disableElevation sx={{ height: 40 }}>
          <Button
            onClick={handleMostrar}
            disabled={!serviceCode.trim() || loading}
            sx={{
              bgcolor: '#1a7a4a', color: 'white', fontWeight: 600, px: 2.5,
              '&:hover': { bgcolor: '#155e39' },
              '&.Mui-disabled': { bgcolor: '#a5d6b8', color: 'white' },
            }}
          >
            {loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'MOSTRAR'}
          </Button>
          <Button
            size="small"
            onClick={e => setMenuAnchor(e.currentTarget)}
            sx={{
              bgcolor: '#1a7a4a', color: 'white', px: 0.5, minWidth: 32,
              borderLeft: '1px solid rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: '#155e39' },
            }}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          disablePortal={false}
          style={{ zIndex: 9999 }}
          PaperProps={{ sx: { minWidth: 180, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}
        >
          <MenuItem onClick={handleMostrar} sx={{ fontSize: '0.875rem' }}>Mostrar</MenuItem>
          <MenuItem onClick={handleExport} disabled={!rows.length} sx={{ fontSize: '0.875rem' }}>Exportar</MenuItem>
        </Menu>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'white' }}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.8rem', color: '#424242' }} />
                {visibleCols.map(col => (
                  <TableCell key={col.key} sx={{ bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.8rem', color: '#424242', whiteSpace: 'nowrap' }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={visibleCols.length + 1} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                    <Box sx={{ mt: 1, fontSize: '0.8rem', color: '#9e9e9e' }}>
                      Buscando en historial de dispositivos...
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading && searched && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleCols.length + 1} align="center" sx={{ py: 4, color: '#9e9e9e', fontSize: '0.875rem' }}>
                    Sin datos
                  </TableCell>
                </TableRow>
              )}
              {!loading && !searched && (
                <TableRow>
                  <TableCell colSpan={visibleCols.length + 1} align="center" sx={{ py: 4, color: '#bdbdbd', fontSize: '0.875rem' }}>
                    Ingresa un código de servicio y presiona MOSTRAR
                  </TableCell>
                </TableRow>
              )}
              {!loading && pagedRows.map((row, idx) => {
                const globalIndex   = page * rowsPerPage + idx;
                const isHighlighted = highlightIndex === globalIndex;
                return (
                  <TableRow
                    key={globalIndex}
                    sx={{
                      bgcolor: isHighlighted ? '#e3f2fd' : 'transparent',
                      '&:hover': { bgcolor: isHighlighted ? '#e3f2fd' : '#f5f5f5' },
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <TableCell sx={{ width: 48, py: 0.75, px: 1 }}>
                      <Tooltip title="Ver en mapa">
                        <IconButton
                          size="small"
                          onClick={() => handleRowHighlight(globalIndex)}
                          sx={{ color: isHighlighted ? '#1976d2' : '#bdbdbd', '&:hover': { color: '#1976d2' }, p: 0.5 }}
                        >
                          <MyLocationIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    {visibleCols.map(col => (
                      <TableCell key={col.key} sx={{ fontSize: '0.8125rem', color: '#333', py: 1, whiteSpace: col.key === 'address' ? 'normal' : 'nowrap', maxWidth: col.key === 'address' ? 240 : 'unset' }}>
                        {getCellValue(row, col.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{ borderTop: '1px solid #e0e0e0', bgcolor: 'white', flexShrink: 0 }}>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Registros por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          sx={{
            '.MuiTablePagination-toolbar': { minHeight: 48 },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.8rem' },
          }}
        />
      </Box>
    </Box>
  );
};

export default RouteReport;
